import { createContext, useCallback, useContext, useState, ReactNode } from "react";
import { Modal } from "../components/ui/modal";
import Button from "../components/ui/button/Button";

interface ConfirmOpcoes {
  title?: string;
  message: string;
  confirmLabel?: string;
  variant?: "danger" | "default";
}

interface Pendente extends ConfirmOpcoes {
  resolve: (v: boolean) => void;
}

interface ConfirmContextValue {
  confirmar: (opcoes: ConfirmOpcoes | string) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

const IconeAlerta = () => (
  <svg className="fill-current" width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M3.6501 12.0001C3.6501 7.38852 7.38852 3.6501 12.0001 3.6501C16.6117 3.6501 20.3501 7.38852 20.3501 12.0001C20.3501 16.6117 16.6117 20.3501 12.0001 20.3501C7.38852 20.3501 3.6501 16.6117 3.6501 12.0001ZM12.0001 1.8501C6.39441 1.8501 1.8501 6.39441 1.8501 12.0001C1.8501 17.6058 6.39441 22.1501 12.0001 22.1501C17.6058 22.1501 22.1501 17.6058 22.1501 12.0001C22.1501 6.39441 17.6058 1.8501 12.0001 1.8501ZM10.9992 7.52517C10.9992 8.07746 11.4469 8.52517 11.9992 8.52517H12.0002C12.5525 8.52517 13.0002 8.07746 13.0002 7.52517C13.0002 6.97289 12.5525 6.52517 12.0002 6.52517H11.9992C11.4469 6.52517 10.9992 6.97289 10.9992 7.52517ZM12.0002 17.3715C11.586 17.3715 11.2502 17.0357 11.2502 16.6215V10.945C11.2502 10.5308 11.586 10.195 12.0002 10.195C12.4144 10.195 12.7502 10.5308 12.7502 10.945V16.6215C12.7502 17.0357 12.4144 17.3715 12.0002 17.3715Z"
    />
  </svg>
);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pendente, setPendente] = useState<Pendente | null>(null);

  const confirmar = useCallback((opcoes: ConfirmOpcoes | string) => {
    const normalizado = typeof opcoes === "string" ? { message: opcoes } : opcoes;
    return new Promise<boolean>((resolve) => {
      setPendente({ ...normalizado, resolve });
    });
  }, []);

  const responder = (v: boolean) => {
    pendente?.resolve(v);
    setPendente(null);
  };

  return (
    <ConfirmContext.Provider value={{ confirmar }}>
      {children}
      <Modal isOpen={!!pendente} onClose={() => responder(false)} className="max-w-md m-4 p-6" showCloseButton={false}>
        {pendente && (
          <>
            <div className="mb-5 flex items-start gap-3">
              <div
                className={`-mt-0.5 ${
                  pendente.variant === "danger" ? "text-error-500" : "text-warning-500"
                }`}
              >
                <IconeAlerta />
              </div>
              <div>
                <h4 className="mb-1 text-base font-semibold text-gray-800 dark:text-white/90">
                  {pendente.title || "Confirmar ação"}
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">{pendente.message}</p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" size="sm" variant="outline" onClick={() => responder(false)}>
                Cancelar
              </Button>
              <Button
                type="button"
                size="sm"
                className={pendente.variant === "danger" ? "!bg-error-500 hover:!bg-error-600" : ""}
                onClick={() => responder(true)}
              >
                {pendente.confirmLabel || "Confirmar"}
              </Button>
            </div>
          </>
        )}
      </Modal>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm precisa estar dentro de <ConfirmProvider>");
  return ctx.confirmar;
}
